import { api } from '../Api';
import { parseJsonResponse } from './clutchApi';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
}

export async function adminGet(url) {
  const response = await fetch(url, { method: 'GET', headers: authHeaders() });
  return parseJsonResponse(response);
}

export async function adminPost(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  return parseJsonResponse(response);
}

export async function adminPut(url, body) {
  const response = await fetch(url, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  return parseJsonResponse(response);
}

export async function adminPatch(url, body) {
  const response = await fetch(url, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  return parseJsonResponse(response);
}

export async function adminDelete(url) {
  const response = await fetch(url, { method: 'DELETE', headers: authHeaders() });
  return parseJsonResponse(response);
}

export function listAdminUsers(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  const qs = query.toString();
  return adminGet(`${api.adminUsers}${qs ? `?${qs}` : ''}`);
}

export function persistAdminUserFromPayload(result) {
  const user = result?.data?.user;
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  }
  return user;
}

export async function refreshStoredAdminUser() {
  const result = await adminGet(api.adminMe);
  return persistAdminUserFromPayload(result);
}

export function normalizeProfileImageFile(file) {
  if (!(file instanceof Blob)) {
    throw new Error('Invalid file.');
  }

  const name = file.name || 'profile.png';
  const ext = name.split('.').pop()?.toLowerCase() || 'png';
  const allowed = ['jpg', 'jpeg', 'png', 'webp'];
  if (!allowed.includes(ext)) {
    throw new Error('Profile image must be PNG, JPG, or WEBP.');
  }

  const maxKb = 2048;
  if (file.size > maxKb * 1024) {
    throw new Error(`Profile image must be ${maxKb} KB or smaller.`);
  }

  const typeByExt = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp'
  };
  const type =
    file.type && file.type.startsWith('image/') ? file.type : typeByExt[ext] || 'image/png';

  if (file instanceof File && file.type === type && file.name === name) {
    return file;
  }

  return new File([file], name, { type, lastModified: file.lastModified || Date.now() });
}

export async function adminUpload(url, formData) {
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: formData
  });
  return parseJsonResponse(response);
}

export const adminApi = {
  listUsers: listAdminUsers,
  me: () => adminGet(api.adminMe),
  updateMyProfile: (name) => adminPatch(api.adminAuthProfile, { name }),
  updateMyPassword: (payload) => adminPut(api.adminAuthPassword, payload),
  updateMyProfileImage: (file) => {
    const normalized = normalizeProfileImageFile(file);
    const formData = new FormData();
    formData.append('image', normalized, normalized.name);
    return adminUpload(api.adminAuthProfileImage, formData);
  },
  sendEmailChangeCurrentOtp: () => adminPost(api.adminEmailChangeCurrentSend, {}),
  verifyEmailChangeCurrentOtp: (otp) => adminPost(api.adminEmailChangeCurrentVerify, { otp }),
  requestEmailChange: (newEmail) => adminPost(api.adminEmailChangeNew, { new_email: newEmail }),
  verifyEmailChangeNewOtp: (otp) => adminPost(api.adminEmailChangeNewVerify, { otp }),
  userSummary: (userId) => adminGet(api.adminUserSummary(userId)),
  userInsights: (userId) => adminGet(api.adminUserInsights(userId)),
  userWorkload: (userId) => adminGet(api.adminUserWorkload(userId)),
  userSessions: (userId, params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });
    const qs = query.toString();
    return adminGet(`${api.adminUserSessions(userId)}${qs ? `?${qs}` : ''}`);
  },
  userOnboarding: (userId) => adminGet(api.adminUserOnboarding(userId)),
  userWellness: (userId, params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });
    const qs = query.toString();
    return adminGet(`${api.adminUserWellness(userId)}${qs ? `?${qs}` : ''}`);
  },
  blockUser: (userId, isBlocked) => adminPatch(api.adminUserStatus(userId), { is_blocked: isBlocked }),
  deleteUser: (userId) => adminDelete(api.adminUser(userId)),
  changePassword: (userId, password, passwordConfirmation) =>
    adminPut(api.adminUserPassword(userId), {
      password,
      password_confirmation: passwordConfirmation
    }),
  listRoles: () => adminGet(api.adminRoles),
  listPermissions: () => adminGet(api.adminPermissions),
  rolePermissions: (roleId) => adminGet(api.adminRolePermissions(roleId))
};
